// src/pages/Premium.tsx
import React from "react";
import { useAuth } from "../context/AuthContext";
import {
  Star,
  User,
  Users,
  GraduationCap,
  Check,
  AlertCircle,
} from "lucide-react";

type PlanKey = "individual" | "family" | "school";

const PLANS: {
  key: PlanKey;
  title: string;
  subtitle: string;
  price: string;
  description: string;
  bulletPoints: string[];
  ctaLabel: string;
}[] = [
  {
    key: "individual",
    title: "MixMind Premium",
    subtitle: "Pour un utilisateur",
    price: "4,90 € / mois*",
    description:
      "Idéal pour une personne qui veut structurer sa curiosité, suivre sa progression et profiter d’un confort d’usage renforcé.",
    bulletPoints: [
      "Mode sombre confort par défaut",
      "Accès prioritaire aux futurs exercices interactifs",
      "Suggestions de contenus personnalisés",
      "Statistiques détaillées de curiosité",
    ],
    ctaLabel: "Activer Premium individuel",
  },
  {
    key: "family",
    title: "MixMind Family Premium",
    subtitle: "Pour les familles",
    price: "7,90 € / mois*",
    description:
      "Pour les foyers qui veulent un espace partagé parent/enfant, avec des profils séparés et une vraie visibilité sur les contenus.",
    bulletPoints: [
      "Jusqu’à 8 profils (dont 2 parents maximum)",
      "Rapports simplifiés par profil",
      "Filtres avancés sur les contenus enfants",
      "Mode “découverte en famille”",
    ],
    ctaLabel: "Activer Family Premium",
  },
  {
    key: "school",
    title: "MixMind School Premium",
    subtitle: "Pour les enseignants & classes",
    price: "12,90 € / mois*",
    description:
      "Pensé pour un usage en petit groupe : enseignants, ateliers, classes, avec des profils élève et un suivi pédagogique simplifié.",
    bulletPoints: [
      "Profils enseignant + élèves",
      "Tableau de bord par groupe",
      "Sélection de contenus pour la classe",
      "Fonctionnalités pédagogiques à venir",
    ],
    ctaLabel: "Activer School Premium",
  },
];

export default function Premium() {
  const {
    account,
    isAuthed,
    isIndividual,
    isFamily,
    isSchool,
    isPremium,
    activatePremium,
  } = useAuth();

  function handleActivate(plan: PlanKey) {
    if (!account) {
      // La logique plus complète est dans AuthContext (alert + blocage)
      activatePremium(plan);
      return;
    }
    activatePremium(plan);
  }

  const currentType = isIndividual
    ? "individual"
    : isFamily
    ? "family"
    : isSchool
    ? "school"
    : null;

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold">Premium</h1>
        <p className="text-sm text-slate-700 max-w-3xl">
          MixMind Premium reste une démo pour l’instant : aucun paiement réel,
          mais une vision claire de ce que pourraient offrir les différentes
          formules pour un individu, une famille ou une structure scolaire.
        </p>

        {!isAuthed && (
          <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800">
            <AlertCircle className="w-3 h-3" />
            <span>
              Pour tester l’activation des plans, connecte-toi d’abord (ou
              utilise un compte de démo).
            </span>
          </div>
        )}
      </header>

      <section className="grid gap-5 md:grid-cols-3">
        {PLANS.map((plan) => {
          const isCurrentType = currentType === plan.key;
          const isDisabledType =
            currentType !== null && currentType !== plan.key;

          const Icon =
            plan.key === "individual"
              ? User
              : plan.key === "family"
              ? Users
              : GraduationCap;

          const isFamilyPlan = plan.key === "family";

          return (
            <div
              key={plan.key}
              className={`relative rounded-2xl border p-5 shadow-sm flex flex-col gap-3 ${
                isFamilyPlan
                  ? "bg-slate-50 text-slate-900 border-blue-300 shadow-md dark:bg-slate-900 dark:text-slate-50 dark:border-blue-400/80"
                  : "mm-surface"
              }`}
            >
              {plan.key === "family" && (
                <div className="absolute -top-3 right-4 inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-[11px] font-semibold px-2 py-0.5 shadow-sm">
                  <Star className="w-3 h-3" />
                  <span>Le plus adapté</span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-2xl flex items-center justify-center bg-blue-600 text-white dark:bg-blue-500">
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">{plan.title}</p>
                  <p className="text-[11px] text-slate-600 dark:text-slate-400">{plan.subtitle}</p>
                </div>
              </div>

              <p className="text-lg font-semibold text-slate-900 dark:text-slate-50">{plan.price}</p>
              <p className="text-[11px] text-slate-600 dark:text-slate-400">
                *Tarifs fictifs à titre indicatif uniquement.
              </p>

              <p className="text-xs text-slate-700 dark:text-slate-200">{plan.description}</p>

              <ul className="mt-1 space-y-1 text-[11px] text-slate-700 dark:text-slate-200">
                {plan.bulletPoints.map((b) => (
                  <li key={b} className="flex items-start gap-1.5">
                    <Check className="w-3 h-3 mt-[2px] text-emerald-500" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => handleActivate(plan.key)}
                  disabled={isDisabledType}
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium border transition ${
                    isDisabledType
                      ? "border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed"
                      : "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  <Star className="w-3 h-3" />
                  <span>{plan.ctaLabel}</span>
                </button>
                {isDisabledType && (
                  <p className="mt-1 text-[11px] text-slate-500">
                    Ce plan est réservé aux{" "}
                    {plan.key === "individual"
                      ? "comptes individuels"
                      : plan.key === "family"
                      ? "comptes famille"
                      : "comptes school"}
                    . Passe par{" "}
                    <span className="font-medium">
                      Profil → Gestion du compte
                    </span>{" "}
                    pour changer de type.
                  </p>
                )}
                {isCurrentType && isPremium && (
                  <p className="mt-1 text-[11px] text-emerald-600">
                    Ce plan est déjà actif sur ton compte (vitrine).
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}

